import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../services/api'

interface User {
  id: string
  username: string
  email: string
  role: 'super_admin' | 'tenant_admin' | 'user'
  tenant_id: string | null
}

interface Tenant {
  id: string
  name: string
  domain: string
  is_active: boolean
}

interface LoginResult {
  success: boolean
  requires_2fa: boolean
  temp_token?: string
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  selectedTenant: Tenant | null
  login: (username: string, password: string) => Promise<LoginResult>
  login2fa: (tempToken: string, code: string) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  setSelectedTenant: (tenant: Tenant | null) => void
  getEffectiveTenantId: () => string | null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      selectedTenant: null,

      login: async (username: string, password: string): Promise<LoginResult> => {
        const response = await api.post('/auth/login',
          `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`,
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        )

        const { access_token, refresh_token, requires_2fa, temp_token } = response.data

        // Check if 2FA is required
        if (requires_2fa) {
          return { success: false, requires_2fa: true, temp_token }
        }

        set({ token: access_token, refreshToken: refresh_token, isAuthenticated: true })

        // Get user info
        const userResponse = await api.get('/auth/me', {
          headers: { Authorization: `Bearer ${access_token}` }
        })
        set({ user: userResponse.data })

        return { success: true, requires_2fa: false }
      },

      login2fa: async (tempToken: string, code: string) => {
        const response = await api.post('/auth/login/2fa', {
          temp_token: tempToken,
          code: code
        })

        const { access_token, refresh_token } = response.data
        set({ token: access_token, refreshToken: refresh_token, isAuthenticated: true })

        // Get user info
        const userResponse = await api.get('/auth/me', {
          headers: { Authorization: `Bearer ${access_token}` }
        })
        set({ user: userResponse.data })
      },

      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          selectedTenant: null
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      setSelectedTenant: (tenant: Tenant | null) => {
        set({ selectedTenant: tenant })
      },

      // Returns the effective tenant ID to use for API calls
      getEffectiveTenantId: () => {
        const state = get()
        if (state.user?.role === 'super_admin') {
          return state.selectedTenant?.id || null
        }
        return state.user?.tenant_id || null
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        selectedTenant: state.selectedTenant
      })
    }
  )
)
